import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import { PARTNER_IDS_WITH_YARD_SIGN_BOTTOM_RIDER } from 'common/jobConstants';

import { SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR } from 'api/actionTypes';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import {
  INVENTORY_ITEM_STATUSES_OPTIONS,
  INVENTORY_ITEM_TYPE_YARDSIGN_FRAME,
  INVENTORY_ITEM_TYPE_YARDSIGN_PANEL,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_3Z,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_STONE_OAK,
  INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP,
} from 'common/inventoryConstants';

import { PARTNER_ID_3Z_PROPERTIES, PARTNER_ID_STONE_OAK } from 'common/partnerConstants';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import ButtonDeprecated, { BUTTON_THEME_BLUE_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const YARD_SIGN_STATE_INITIAL = 'YARD_SIGN_STATE_INITIAL';
const YARD_SIGN_STATE_ITEMS_SCANNED = 'YARD_SIGN_STATE_ITEMS_SCANNED';

const getFormFields = ({ partnerId, setIsBarcodeScannerOpen, setFocusedField }) => {
  const shouldAskForBottomRider = PARTNER_IDS_WITH_YARD_SIGN_BOTTOM_RIDER.includes(partnerId);
  return [
    {
      id: 'yardSignPanelSerialNumber',
      title: 'Panel Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignPanelSerialNumber');
        },
      },
    },
    {
      id: 'yardSignPanelStatus',
      title: 'Panel Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    },
    {
      id: 'yardSignTopRiderSerialNumber',
      title: 'Sunroom Top Rider Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignTopRiderSerialNumber');
        },
      },
    },
    {
      id: 'yardSignTopRiderStatus',
      title: 'Top Rider Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    },
    {
      id: 'yardSignFrameSerialNumber',
      title: 'Frame Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignFrameSerialNumber');
        },
      },
    },
    {
      id: 'yardSignFrameStatus',
      title: 'Frame Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    },
    showFieldIf(shouldAskForBottomRider, {
      id: 'yardSignBottomRiderSerialNumber',
      title: 'Bottom Rider Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('yardSignBottomRiderSerialNumber');
        },
      },
    }),
    showFieldIf(shouldAskForBottomRider, {
      id: 'yardSignBottomRiderStatus',
      title: 'Bottom Rider Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    }),
  ];
};

const SrSunSetupDriverJobYardSignTakedown = ({
  formikBag,
  partnerId,
  itemScans,
  listingAssignedItems,
  getCurrentLocation,
  requestGeolocationPermission,
  handleYardSignTakeDownScan,
}) => {
  const [viewState, setViewState] = useState(
    itemScans.length > 0 ? YARD_SIGN_STATE_ITEMS_SCANNED : YARD_SIGN_STATE_INITIAL,
  );

  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const dispatch = useDispatch();

  const _createGeolocationError = ({ errorMessage }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
      },
    });
  };

  const _requestGeolocationPermission = async () => {
    setIsLocationLoading(true);
    try {
      await requestGeolocationPermission();
      const location = await getCurrentLocation();

      setIsLocationLoading(false);
      return location;
    } catch (error) {
      _createGeolocationError({
        errorMessage: error.message,
      });
      setIsLocationLoading(false);
      throw error;
    }
  };

  const getBottomRiderType = () => {
    switch (partnerId) {
      case PARTNER_ID_3Z_PROPERTIES:
        return INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_3Z;
      case PARTNER_ID_STONE_OAK:
        return INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_STONE_OAK;
      default:
        return INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM;
    }
  };

  const _onSubmit = async (formValues) => {
    const location = await _requestGeolocationPermission();
    const { latitude, longitude } = location;
    const {
      yardSignPanelSerialNumber,
      yardSignPanelStatus,
      yardSignFrameSerialNumber,
      yardSignFrameStatus,
      yardSignTopRiderSerialNumber,
      yardSignTopRiderStatus,
      yardSignBottomRiderSerialNumber,
      yardSignBottomRiderStatus,
    } = formValues;

    const itemGroup = lodashCompact([
      yardSignPanelSerialNumber && {
        type: INVENTORY_ITEM_TYPE_YARDSIGN_PANEL,
        serialNumber: yardSignPanelSerialNumber,
        status: yardSignPanelStatus,
      },
      yardSignFrameSerialNumber && {
        type: INVENTORY_ITEM_TYPE_YARDSIGN_FRAME,
        serialNumber: yardSignFrameSerialNumber,
        status: yardSignFrameStatus,
      },
      yardSignTopRiderSerialNumber && {
        type: INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP,
        serialNumber: yardSignTopRiderSerialNumber,
        status: yardSignTopRiderStatus,
      },
      yardSignBottomRiderSerialNumber && {
        type: getBottomRiderType(),
        serialNumber: yardSignBottomRiderSerialNumber,
        status: yardSignBottomRiderStatus,
      },
    ]);

    handleYardSignTakeDownScan({
      itemGroup,
      latitude,
      longitude,
    }).then(() => {
      if (itemScans.length > 0) {
        setViewState(YARD_SIGN_STATE_ITEMS_SCANNED);
      }
    });
  };

  const _onFieldScan = ({ barcode, formikBag }) => {
    if (!focusedField) {
      return;
    }
    formikBag.setFieldValue(focusedField, barcode);
    setFocusedField(null);
    setIsBarcodeScannerOpen(false);
  };

  const _handleScannerClose = () => {
    setIsBarcodeScannerOpen(false);
    setFocusedField(null);
  };

  const getItemScansSerialNumberMapping = () => {
    const itemScansSerialNumberMapping = {};
    itemScans.forEach((itemScan) => {
      itemScansSerialNumberMapping[itemScan.inventoryItemTypeId] = {
        serialNumber: itemScan.serialNumber,
        status: itemScan.status,
      };
    });
    return itemScansSerialNumberMapping;
  };

  const itemScansSerialNumberMapping = getItemScansSerialNumberMapping();
  const shouldAskForBottomRider = PARTNER_IDS_WITH_YARD_SIGN_BOTTOM_RIDER.includes(partnerId);
  const bottomRiderType = getBottomRiderType();
  const initialValues = {
    yardSignPanelSerialNumber:
      listingAssignedItems[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.serialNumber ||
      null,
    yardSignPanelStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.status ||
      null,
    yardSignTopRiderSerialNumber:
      listingAssignedItems[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]?.serialNumber ||
      null,
    yardSignTopRiderStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]?.status ||
      null,
    yardSignFrameSerialNumber:
      listingAssignedItems[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.serialNumber ||
      null,
    yardSignFrameStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.status ||
      null,
    yardSignBottomRiderSerialNumber:
      listingAssignedItems[bottomRiderType]?.serialNumber ||
      itemScansSerialNumberMapping[bottomRiderType]?.serialNumber ||
      null,
    yardSignBottomRiderStatus:
      listingAssignedItems[bottomRiderType]?.status ||
      itemScansSerialNumberMapping[bottomRiderType]?.status ||
      null,
  };

  return (
    <div styleName="card" className="fieldsContainer" id="field-yardSignUrl">
      <span styleName="cardTitle">Register Yard Sign</span>

      <SrBox margin="16px 0 0">
        {viewState === YARD_SIGN_STATE_ITEMS_SCANNED && (
          <>
            <SrBox styleName="jobDetailsBox" padding="16px 0 16px">
              <SrBox marginBottom="16px">
                <SrBox styleName="title">Panel Barcode Number</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_PANEL]?.serialNumber ||
                    '--'}
                </SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px" marginBottom="16px">
                <SrBox styleName="title">Sunroom Top Rider Barcode Number</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_TOP]
                    ?.serialNumber || '--'}
                </SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px">
                <SrBox styleName="title">Frame Barcode Number</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_FRAME]?.serialNumber ||
                    '--'}
                </SrBox>
              </SrBox>
              {shouldAskForBottomRider && (
                <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px" marginTop="16px">
                  <SrBox styleName="title">Bottom Rider Barcode Number</SrBox>
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM]
                    ?.status && (
                    <SrBox styleName="status">
                      Status:&nbsp;
                      {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM]
                        ?.status || '--'}
                    </SrBox>
                  )}
                  <SrBox styleName="serialNumber">
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_YARDSIGN_RIDER_BOTTOM]
                      ?.serialNumber || '--'}
                  </SrBox>
                </SrBox>
              )}
            </SrBox>
            <SrBox margin="16px 0 0">
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(YARD_SIGN_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </>
        )}
        {viewState === YARD_SIGN_STATE_INITIAL && (
          <Formik
            onSubmit={_onSubmit}
            initialValues={createInitialValues({
              formFields: getFormFields({
                partnerId,
                setIsBarcodeScannerOpen,
                setFocusedField,
              }),
              defaultValues: initialValues,
            })}
            validate={createFormValidationFn(
              getFormFields({
                partnerId,
                setIsBarcodeScannerOpen,
                setFocusedField,
              }),
            )}
            render={(formikBag) => {
              const yardSignUrlFormFields = getFormFields({
                partnerId,
                setIsBarcodeScannerOpen,
                setFocusedField,
              });
              return (
                <Form>
                  {yardSignUrlFormFields.map((formField) => (
                    <SrFormikField
                      formikBag={formikBag}
                      formField={formField}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                    />
                  ))}
                  {isBarcodeScannerOpen && (
                    <SrBarcodeScanner
                      onText={(barcode) => _onFieldScan({ barcode, formikBag })}
                      onClose={_handleScannerClose}
                    />
                  )}
                  <ButtonDeprecated
                    margin="24px 0 0"
                    type="submit"
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                    disabled={isLocationLoading}
                  >
                    {isLocationLoading ? 'Loading...' : 'Submit'}
                  </ButtonDeprecated>
                </Form>
              );
            }}
          />
        )}
      </SrBox>
      <SrFormikErrorMessage id="yardSignUrl" formikBag={formikBag} />
    </div>
  );
};

SrSunSetupDriverJobYardSignTakedown.propTypes = {
  formikBag: PropTypes.object.isRequired,
  partnerId: PropTypes.string.isRequired,
  itemScans: PropTypes.array.isRequired,
  getCurrentLocation: PropTypes.func.isRequired,
  requestGeolocationPermission: PropTypes.func.isRequired,
  handleYardSignTakeDownScan: PropTypes.func.isRequired,
};

SrSunSetupDriverJobYardSignTakedown.displayName = 'SrSunSetupDriverJobYardSignTakedown';

export default SrSunSetupDriverJobYardSignTakedown;
